import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, inputStyle, btnPrimary, FormRow } from './AcademyShared';

const PREFIX_FIELDS = [
  ['category_prefix', 'category_number_length', 'Category'],
  ['bootcamp_type_prefix', 'bootcamp_type_number_length', 'Bootcamp Type'],
  ['cohort_prefix', 'cohort_number_length', 'Cohort'],
  ['student_prefix', 'student_number_length', 'Student'],
  ['enrollment_prefix', 'enrollment_number_length', 'Enrollment'],
  ['session_prefix', 'session_number_length', 'Session'],
  ['assessment_prefix', 'assessment_number_length', 'Assessment'],
  ['instructor_prefix', 'instructor_number_length', 'Instructor'],
  ['payment_prefix', 'payment_number_length', 'Receipt'],
  ['income_prefix', 'income_number_length', 'Income'],
  ['expense_prefix', 'expense_number_length', 'Expense'],
  ['certificate_prefix', 'certificate_number_length', 'Certificate'],
  ['document_prefix', 'document_number_length', 'Document'],
];

const JSON_FIELDS = [
  ['cohort_rules', 'Cohort Rules'],
  ['enrollment_statuses', 'Enrollment Statuses'],
  ['session_statuses', 'Session Statuses'],
  ['attendance_statuses', 'Attendance Statuses'],
  ['grading_rules', 'Grading Rules'],
  ['payment_rules', 'Payment Rules'],
  ['instructor_compensation_rules', 'Instructor Compensation Rules'],
  ['financial_categories', 'Financial Categories'],
  ['certificate_settings', 'Certificate Settings'],
  ['document_settings', 'Document Settings'],
  ['notification_settings', 'Notification Settings'],
];

export default function AcademySettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [form, setForm] = useState({ number_separator: '-' });
  const [jsonText, setJsonText] = useState({});
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/settings`, { headers: getAuthHeaders() });
      const json = await res.json();
      setForm(json);
      const jt = {};
      JSON_FIELDS.forEach(([f]) => { jt[f] = JSON.stringify(json[f] ?? {}, null, 2); });
      setJsonText(jt);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const u = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const submit = async () => {
    setSaving(true); setMsg('');
    try {
      const body = { ...form };
      for (const [f] of JSON_FIELDS) {
        try { body[f] = JSON.parse(jsonText[f]); } catch { setMsg(`Invalid JSON in ${f}`); setSaving(false); return; }
      }
      const res = await fetch(`${API}/companies/${companyId}/academy/settings`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save');
      setMsg('Settings saved successfully.');
      fetchSettings();
    } catch (e) { setMsg(e.message); }
    setSaving(false);
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚙️ Academy Settings</h2>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
      {msg && <div style={{ marginBottom: 16, fontSize: 13, color: msg.includes('success') ? '#10b981' : '#ef4444' }}>{msg}</div>}

      <Section title="Numbering">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <FormRow label="Number Separator"><input value={form.number_separator || ''} onChange={e => u('number_separator', e.target.value)} style={inputStyle} /></FormRow>
          {PREFIX_FIELDS.map(([pf, lf, label]) => (
            <React.Fragment key={pf}>
              <FormRow label={`${label} Prefix`}><input value={form[pf] || ''} onChange={e => u(pf, e.target.value)} style={inputStyle} /></FormRow>
              <FormRow label={`${label} Number Length`}><input type="number" value={form[lf] || ''} onChange={e => u(lf, e.target.value)} style={inputStyle} /></FormRow>
            </React.Fragment>
          ))}
        </div>
      </Section>

      <Section title="Business Rules (JSON Configuration)">
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Advanced configuration for module behavior. Edit as JSON.</div>
        {JSON_FIELDS.map(([f, label]) => (
          <FormRow key={f} label={label}>
            <textarea style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace', fontSize: 12 }}
              value={jsonText[f] ?? '{}'} onChange={e => setJsonText({ ...jsonText, [f]: e.target.value })} />
          </FormRow>
        ))}
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 40 }}>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginTop: 0, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}
